import {Injectable, OnDestroy} from "@angular/core";
import {debounceTime, ReplaySubject, Subject, Subscription} from "rxjs";
import {MainEvent} from "./MainEvent";

/**
 * Main event bus is meant to be the only event bus for an entire feature/screen.
 * This is different from ControllerEventBus that is more component-based.
 * It is also different from AppEventBus which is intended to be a singleton instance
 * event bus for the entire application.
 *
 * This event bus allows for controller-to-controller communication across any boundaries. It
 * should always be provided either inside a feature module or the root component of a particular
 * screen.
 */
@Injectable()
export class MainEventBus implements OnDestroy {

  private readonly WINDOW_TIME = 3000;
  private readonly eventBus$: Map<string, Subject<MainEvent>>;

  constructor() {
    this.eventBus$ = new Map();

    console.log(`Main event bus created`);
  }

  protected getOrCreateTopic(topicName: string): Subject<MainEvent> {
    if(this.eventBus$.has(topicName)) {
      return this.eventBus$.get(topicName)!;
    }
    else {
      console.log(`Creating main event topic: ${topicName}`);

      const subject = new ReplaySubject<MainEvent>(100, this.WINDOW_TIME);
      this.eventBus$.set(topicName, subject);

      return subject;
    }
  }

  dispatch(event: MainEvent): void {
    console.debug(`Dispatching main event: ${event.eventName}`);
    this.getOrCreateTopic(event.eventName).next(event);
  }

  subscribe(eventName: string,
            handler: (event: MainEvent) => void): Subscription {
    return this.getOrCreateTopic(eventName).subscribe((event) => {
      this.internalHandle(eventName, event, handler);
    });
  }

  subscribeWithDebounce(eventName: string,
                        time: number,
                        handler: (event: MainEvent) => void): Subscription {
    return this.getOrCreateTopic(eventName).pipe(debounceTime(time)).subscribe((event) => {
      this.internalHandle(eventName, event, handler);
    });
  }

  private internalHandle(eventName: string,
                         event: MainEvent,
                         handler: (event: MainEvent) => void) {
    if(event.eventName === eventName) {
      try {
        console.debug(`Handling main event: ${event.eventName}`);
        handler(event);
      }
      catch(error) {
        console.error(`Failed to handle main event: ${event.eventName}`);
      }
    }
  }

  ngOnDestroy() {
    console.log(`Destroying main event bus`);

    this.eventBus$.forEach((subject, topic) => {
      subject.complete();
    });

    this.eventBus$.clear();
  }
}
